import React, { useCallback, useEffect, useState } from "react";

import { faArrowDownAZ } from "@fortawesome/pro-solid-svg-icons/faArrowDownAZ";
import { faGripVertical } from "@fortawesome/pro-solid-svg-icons/faGripVertical";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Row from "antd/es/row";
import cn from "classnames";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useIntl } from "react-intl";

import OptionValue from "@mapmycustomers/shared/types/customField/OptionValue";
import { Button, Popconfirm, TextField } from "@mapmycustomers/ui";

import DefaultCustomFieldFormValues from "@app/scene/settings/component/CustomFields/type/DefaultCustomFieldFormValues";
import layout from "@app/styles/layout";

import { messages } from "./messages";
import styles from "./Options.module.scss";
import { useOptionValidationRules } from "./validation-hooks";

interface Props {
  calculatedField?: boolean;
  form: FormInstance<DefaultCustomFieldFormValues>;
}

const Options: React.FC<Props> = ({ calculatedField, form }) => {
  const intl = useIntl();
  const optionRules = useOptionValidationRules(intl, form);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [onDragStyle, setDragStyle] = useState<boolean>(false);

  useEffect(() => {
    const sortedOptions = form
      .getFieldValue("options")
      .slice()
      .sort((a: OptionValue, b: OptionValue) => a.displayOrder - b.displayOrder);

    form.setFieldsValue({
      options: sortedOptions,
    });
  }, [form]);

  const moveItemFromTo = useCallback(
    (index: number, destinationIndex: number) => {
      const newOptions = [...form.getFieldValue("options")];
      const [removed] = newOptions.splice(index, 1);
      newOptions.splice(destinationIndex, 0, removed);
      form.setFieldsValue({
        options: newOptions,
      });
    },
    [form]
  );

  const onDragEnd = useCallback(
    (result) => {
      setDragStyle(false);
      if (!result.destination) {
        return;
      }
      moveItemFromTo(result.source.index, result.destination.index);
    },
    [moveItemFromTo]
  );

  const onDragStart = useCallback(() => {
    setDragStyle(true);
  }, []);

  const moveToTop = useCallback(
    (index: number) => {
      moveItemFromTo(index, 0);
    },
    [moveItemFromTo]
  );

  const moveToBottom = useCallback(
    (index: number) => {
      const destinationIndex = form.getFieldValue("options").length - 1;
      moveItemFromTo(index, destinationIndex);
    },
    [form, moveItemFromTo]
  );

  const handleAlphabetizeSorting = useCallback(() => {
    const options = [...form.getFieldValue("options")];
    const sortedOptions = options.sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });

    form.setFieldsValue({
      options: sortedOptions,
    });
  }, [form]);

  const handleSort = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setTooltipVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  return (
    <Form.List name="options">
      {(fields, { add, remove }) => {
        const options: OptionValue[] = form.getFieldValue("options");
        const maxValue = Math.max(...options.map(({ value }) => value));
        const handleAdd = () =>
          add({ displayName: "", displayOrder: options.length + 1, value: maxValue + 1 });
        return (
          <Row className={styles.optionsContainer}>
            <Row
              align="middle"
              className={cn(styles.fullWidth, styles.marginBottom)}
              justify="space-between"
            >
              <Col>{intl.formatMessage(messages.title)}</Col>
              {fields.length > 1 && !calculatedField && (
                <Col>
                  <Popconfirm
                    cancelText={intl.formatMessage(messages.popupDiscardButton)}
                    description={intl.formatMessage(messages.sortPopConfirm)}
                    okText={intl.formatMessage(messages.popupYesButton)}
                    onConfirm={handleAlphabetizeSorting}
                    overlayClassName={styles.sortConfirm}
                    placement="leftBottom"
                  >
                    <Tooltip
                      open={tooltipVisible}
                      placement="top"
                      title={intl.formatMessage(messages.sortTooltip)}
                    >
                      <Button
                        className={styles.sortButton}
                        leftIcon={<FontAwesomeIcon icon={faArrowDownAZ} />}
                        onClick={handleSort}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        type="link"
                      >
                        {intl.formatMessage(messages.sortOption)}
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Col>
              )}
            </Row>
            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    className={styles.fullWidth}
                    ref={provided.innerRef}
                  >
                    {fields.map((field, index) => {
                      return (
                        <Draggable draggableId={field.key.toString()} index={index} key={field.key}>
                          {(provided) => (
                            <div {...provided.draggableProps} ref={provided.innerRef}>
                              <Row
                                className={cn(styles.fullWidth, styles.option)}
                                gutter={[layout.spacerS, layout.spacerS]}
                                justify="space-between"
                                key={field.key}
                              >
                                {fields.length > 1 && !calculatedField && (
                                  <Col
                                    className={styles.handle}
                                    span={1}
                                    {...provided.dragHandleProps}
                                  >
                                    <Dropdown
                                      menu={{
                                        items: [
                                          {
                                            className: styles.moveButton,
                                            key: "moveToTop",
                                            label: intl.formatMessage(messages.moveToTopButton),
                                            onClick: () => moveToTop(field.key),
                                          },
                                          {
                                            className: styles.moveButton,
                                            key: "moveToBottom",
                                            label: intl.formatMessage(messages.moveToBottomButton),
                                            onClick: () => moveToBottom(field.key),
                                          },
                                        ],
                                      }}
                                      placement="bottomLeft"
                                      trigger={["contextMenu"]}
                                    >
                                      <Tooltip
                                        placement="left"
                                        title={intl.formatMessage(messages.dragAndDropTooltip)}
                                        trigger={["hover"]}
                                      >
                                        <FontAwesomeIcon
                                          className={styles.dragIcon}
                                          icon={faGripVertical}
                                        />
                                      </Tooltip>
                                    </Dropdown>
                                  </Col>
                                )}
                                <Col flex="auto">
                                  <Form.Item name={[field.name, "displayName"]} rules={optionRules}>
                                    <TextField
                                      locked={calculatedField}
                                      placeholder={intl.formatMessage(messages.placeholder)}
                                      size="large"
                                    />
                                  </Form.Item>
                                </Col>
                                {fields.length > 1 && !calculatedField && (
                                  <Col span={1}>
                                    <Popconfirm
                                      cancelText={intl.formatMessage(messages.goBack)}
                                      description={intl.formatMessage(messages.popconfirmTitle)}
                                      okText={intl.formatMessage(messages.confirm)}
                                      onConfirm={() => remove(index)}
                                      overlayClassName={styles.deleteConfirm}
                                      placement="leftTop"
                                    >
                                      <FontAwesomeIcon
                                        className={styles.deleteIcon}
                                        icon={faTrash}
                                      />
                                    </Popconfirm>
                                  </Col>
                                )}
                              </Row>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {!calculatedField && (
              <Col>
                <Button
                  className={cn(onDragStyle ? styles.onDragStart : styles.onDragEnd)}
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={handleAdd}
                  type="link"
                >
                  {intl.formatMessage(messages.addOption)}
                </Button>
              </Col>
            )}
          </Row>
        );
      }}
    </Form.List>
  );
};

export default Options;
